import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { HiGlobe } from 'react-icons/hi'
import { PiMapPinFill } from 'react-icons/pi'
import {
  ContactCompanySearchResult,
  DoorSearchResult,
} from '../../models/companies'
import { formatInteger } from '../../utils/formatting'
import { MobileTempDetailModal } from './MobileTempDetailModal'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import { ContactCompanyType } from '../../models/contact_companies'

interface MobileSearchResultCardProps {
  result: ContactCompanySearchResult | DoorSearchResult
  searchType:
    | 'fb_location'
    | 'fb_company'
    | 'custom_location'
    | 'custom_company'
  onClick?: (company: ContactCompanySearchResult | DoorSearchResult) => void
}

export const MobileSearchResultCard: React.FC<MobileSearchResultCardProps> = ({
  result,
  searchType,
  onClick,
}) => {
  const navigate = useNavigate()
  const { preferences } = usePreferences()
  const metaLineRef = useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = useState<number>(300) // default fallback
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState<'company' | 'door'>('door')
  const [modalId, setModalId] = useState<string | number>('')
  const [modalName, setModalName] = useState<string>('')

  // Measure available width for state summary
  useEffect(() => {
    const measureWidth = () => {
      if (metaLineRef.current) {
        const width = metaLineRef.current.offsetWidth
        setAvailableWidth(width)
      }
    }

    measureWidth()
    window.addEventListener('resize', measureWidth)
    return () => window.removeEventListener('resize', measureWidth)
  }, [])

  const isLocationSearch =
    searchType === 'fb_location' || searchType === 'custom_location'
  const isCompanySearch =
    searchType === 'fb_company' || searchType === 'custom_company'

  // Format address
  const getAddress = (): string => {
    if (isLocationSearch) {
      const doorResult = result as DoorSearchResult
      const parts = []
      if (doorResult.address) parts.push(doorResult.address)
      if (doorResult.city) parts.push(doorResult.city)
      if (doorResult.state) parts.push(doorResult.state)
      if (doorResult.zipcode) parts.push(doorResult.zipcode)
      return parts.join(', ')
    } else {
      const companyResult = result as ContactCompanySearchResult
      const parts = []
      if (companyResult.address_line1) parts.push(companyResult.address_line1)
      if (companyResult.city) parts.push(companyResult.city)
      if (companyResult.state) parts.push(companyResult.state)
      if (companyResult.zipcode) parts.push(companyResult.zipcode)
      return parts.join(', ')
    }
  }

  // Get type/subcategory name (e.g., "Restaurants & Bars")
  const getTypeName = (): string | undefined => {
    if ('sub_category_name' in result && result.sub_category_name) {
      return result.sub_category_name
    }
    return undefined
  }

  // Get company name
  const getCompanyName = (): string => {
    if (isLocationSearch) {
      const doorResult = result as DoorSearchResult
      return doorResult.chain_name || result.name
    }
    return result.name
  }

  // Get location name (for door results)
  const getLocationName = (): string | undefined => {
    if (isLocationSearch) {
      const doorResult = result as DoorSearchResult
      return doorResult.name
    }
    return undefined
  }

  // Get location count number (for company results)
  const getLocationCount = (): number | undefined => {
    if (isCompanySearch) {
      const companyResult = result as ContactCompanySearchResult
      if (companyResult.count !== undefined) {
        return companyResult.count
      }
      if (companyResult.size !== undefined) {
        return companyResult.size
      }
    }
    return undefined
  }

  // Format state summary to show preview + count of remaining
  // Dynamically determines how many states to show based on available screen width
  const formatStateSummary = (
    stateSummary: string | undefined,
    chainSize?: number
  ): string | undefined => {
    if (!stateSummary) return undefined

    // Split by comma and trim
    const states = stateSummary.split(',').map((s) => s.trim())

    // If 1 state, always show it
    if (states.length <= 1) {
      return stateSummary
    }

    // Estimate space taken by "Chain Size: X • States: " prefix (if chain size exists)
    // Approximate: 8px per character at 12px font size
    const prefixLength =
      chainSize !== undefined
        ? 'Chain Size: 00000 • States: '.length
        : 'States: '.length
    const prefixWidth = prefixLength * 8

    // Available width for state text (leaving room for "+X" suffix)
    const maxStateWidth = availableWidth - prefixWidth - 40 // 40px buffer for "+X"

    // Calculate how many states we can show based on width (approx 8px per char)
    let currentWidth = 0
    let statesToShow = 0

    for (let i = 0; i < states.length; i++) {
      const stateWidth = (states[i].length + (i > 0 ? 2 : 0)) * 8 // +2 for ", "
      if (currentWidth + stateWidth > maxStateWidth) {
        break
      }
      currentWidth += stateWidth
      statesToShow++
    }

    // Always show at least 1, max 4
    statesToShow = Math.max(1, Math.min(4, statesToShow))

    // If we can show all states, do so
    if (statesToShow >= states.length) {
      return stateSummary
    }

    // Show preview and add "+X"
    const preview = states.slice(0, statesToShow).join(', ')
    const remaining = states.length - statesToShow
    return `${preview}, +${remaining}`
  }

  const typeName = getTypeName()
  const companyName = getCompanyName()
  const locationName = getLocationName()

  return (
    <>
      {isLocationSearch ? (
        <LocationResultCard onClick={onClick} />
      ) : (
        <CompanyResultCard onClick={onClick} />
      )}
      <MobileTempDetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        type={modalType}
        id={modalId}
        name={modalName}
      />
    </>
  )

  // Location result card (door search)
  function LocationResultCard({
    onClick,
  }: {
    onClick?: (company: ContactCompanySearchResult) => void
  }) {
    const address = getAddress()
    const doorResult = result as DoorSearchResult
    const companyProxyId = doorResult.chain_proxy_id

    const handleCardClick = () => {
      if (onClick) {
        onClick(result as ContactCompanySearchResult)
        return
      }
      const doorId =
        doorResult.door_proxy_id || doorResult.item_id || doorResult.id
      if (doorId) {
        setModalType('door')
        setModalId(doorId)
        setModalName(locationName || companyName)
        setModalOpen(true)
      }
    }

    const handleCompanyClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      // Navigate to MobileChainPage using chain_id with prefix
      const chainId = doorResult.chain_id
      if (chainId && preferences?.company_type_prefix_map) {
        const prefix =
          preferences.company_type_prefix_map[ContactCompanyType.Chain]
        const chainName = `${prefix}${chainId}`
        navigate(`/chain/${chainName}`)
      }
    }

    const handleAddressClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
        '_blank',
        'noopener,noreferrer'
      )
    }

    return (
      <div
        onClick={handleCardClick}
        className="block p-3 bg-sage-50 rounded-lg mb-3 cursor-pointer overflow-hidden transition-all duration-100 hover:bg-pistachio-200 hover:shadow-lg active:bg-new-company-pistachio-medium active:duration-50 [-webkit-tap-highlight-color:transparent]"
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div>
              <span className="text-xs text-new-company-gray-500 mb-1 inline">
                Company:{' '}
              </span>
              <div
                className="text-xs text-fb-green-900 underline mb-1 inline"
                style={{
                  cursor: companyProxyId ? 'pointer' : 'default',
                }}
                onClick={handleCompanyClick}
              >
                {companyName}
              </div>
            </div>
            <div className="text-sm font-medium text-new-company-gray-900 mb-1">
              {locationName}
            </div>
          </div>
          {typeName && (
            <span className="px-2.5 py-1 bg-new-company-gray-border text-new-company-gray-900 rounded-xl text-[11px] font-medium whitespace-nowrap flex-shrink-0 ml-2">
              {typeName}
            </span>
          )}
        </div>

        {address && (
          <div
            onClick={handleAddressClick}
            className="flex items-start gap-1.5 text-[13px] text-fb-green-900 underline mt-2 cursor-pointer"
          >
            <span className="text-new-company-gray-500 mt-0.5">
              <PiMapPinFill size={16} />
            </span>
            <div>{address}</div>
          </div>
        )}
      </div>
    )
  }

  // Company result card (chain search)
  function CompanyResultCard({
    onClick,
  }: {
    onClick?: (company: ContactCompanySearchResult) => void
  }) {
    const companyResult = result as ContactCompanySearchResult
    const domain = companyResult.domain
    const locationCount = getLocationCount()
    const formattedStateSummary = formatStateSummary(
      companyResult.state_summary,
      locationCount
    )

    const handleCardClick = () => {
      if (onClick) {
        onClick(companyResult)
        return
      }

      // Navigate to MobileChainPage using item_id (chain ID) with prefix
      const chainId = companyResult.item_id
      if (chainId && preferences?.company_type_prefix_map) {
        const prefix =
          preferences.company_type_prefix_map[ContactCompanyType.Chain]
        const chainName = `${prefix}${chainId}`
        navigate(`/chain/${chainName}`)
      }
    }

    const handleDomainClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      const url = domain.startsWith('http') ? domain : `https://${domain}`
      window.open(url, '_blank', 'noopener,noreferrer')
    }

    return (
      <div
        onClick={handleCardClick}
        className="block p-3 bg-sage-50 mb-1 cursor-pointer overflow-hidden transition-all duration-100 hover:bg-pistachio-200 active:bg-[#E5ECE7] active:duration-50 [-webkit-tap-highlight-color:transparent]"
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="text-sm font-medium text-new-company-gray-900 mb-1">
              {companyName}
            </div>
          </div>
          {typeName && (
            <span className="px-2.5 py-1 bg-new-company-gray-border text-new-company-gray-900 rounded-xl text-[11px] font-medium whitespace-nowrap flex-shrink-0 ml-2">
              {typeName}
            </span>
          )}
        </div>

        {domain && (
          <div className="flex items-start gap-1.5 text-[13px] text-fb-green-900 underline mt-2 cursor-pointer">
            <span className="text-new-company-gray-500 mt-0.5">
              <HiGlobe size={16} />
            </span>
            <div onClick={handleDomainClick}>{domain}</div>
          </div>
        )}

        {(locationCount !== undefined || formattedStateSummary) && (
          <div
            ref={metaLineRef}
            className="flex items-center gap-1.5 text-xs text-new-company-gray-500 mt-1.5 whitespace-nowrap overflow-hidden text-ellipsis"
          >
            {locationCount !== undefined && (
              <span>Chain Size: {formatInteger(locationCount)}</span>
            )}
            {locationCount !== undefined && formattedStateSummary && (
              <span>•</span>
            )}
            {formattedStateSummary && (
              <span>States: {formattedStateSummary}</span>
            )}
          </div>
        )}
      </div>
    )
  }
}
